import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ClockIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  SignalIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { format, subHours, subDays, differenceInMinutes, differenceInHours, startOfDay, startOfHour } from 'date-fns';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import axios from '@utils/axios';
import { useAuth } from '../contexts/AuthContext';
import DropdownMenu from './DropdownMenu';
import { hasRole } from '../config/permissions';
import { useWebSocket } from '../contexts/WebSocketContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

// Synced history is always stored in Celsius, whatever unit the panel screen
// is set to. The panel's own history page converts using its live setting, but
// here the dryer may be offline and there is no setting to read — so the
// viewer picks. Fahrenheit leads because most customers are in the USA.
const toDisplayTemp = (celsius, unit) => {
  const value = parseFloat(celsius);
  if (!Number.isFinite(value)) return null;
  return unit === 'F' ? (value * 9) / 5 + 32 : value;
};

// The download carries every sample of the loaded window, not the averaged
// points on the chart. Temperatures are stored in Celsius and written in the
// unit the viewer has picked, so the column names say which one they got.
const exportColumns = (unit) => [
  { key: 'recordedAt', label: 'Date/Time' },
  { key: 'inletMoisture', label: 'Inlet Moisture (%)' },
  { key: 'outletMoisture', label: 'Outlet Moisture (%)' },
  { key: 'moistureTarget', label: 'Moisture Setpoint (%)' },
  { key: 'dischargeRate', label: 'Discharge Rate' },
  { key: 'apt', label: `Drying Temperature (${unit})`, temperature: true },
  { key: 'inletTemperature', label: `Inlet Temperature (${unit})`, temperature: true },
  { key: 'outletTemperature', label: `Outlet Temperature (${unit})`, temperature: true },
  { key: 'mode', label: 'Mode' },
  { key: 'dryerState', label: 'Dryer State' },
  { key: 'product', label: 'Product' }
];

// vendor mode: fetch through the scoped /vendor history endpoints and drop
// the sync machinery — vendors see the stored data, not how it gets here.
const DeviceHistory = ({ deviceId, vendor = false }) => {
  const [syncDetails, setSyncDetails] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [customDateRange, setCustomDateRange] = useState({
    start: null,
    end: null
  });
  const [dataAvailability, setDataAvailability] = useState(null);
  const [dataAggregation, setDataAggregation] = useState('auto'); // 'auto', 'raw', 'hourly', 'daily'
  const [refreshing, setRefreshing] = useState(false);
  const [showModeOverlay, setShowModeOverlay] = useState(false);
  const [tempUnit, setTempUnit] = useState('F'); // display only — stored data is Celsius
  const { user } = useAuth();
  const { addSSEListener } = useWebSocket();

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        ...(vendor ? [] : [fetchSyncDetails()]),
        fetchDataAvailability()
      ]);
      // Initial data fetch
      await fetchHistoryData();
      setLoading(false);
    };

    init();
  }, [deviceId]);

  // Setup shared SSE listener
  useEffect(() => {
    if (!addSSEListener) return;

    const handleSSEMessage = (data) => {
      if (data.type === 'history_sync_progress' && data.deviceSerial === deviceId) {
        updateSyncProgress(data);
      }
      else if (data.type === 'history_sync_completed' && data.deviceSerial === deviceId) {
        setSyncDetails(prev => ({
          ...prev,
          currentStatus: 'synced',
          lastSyncTime: new Date().toISOString(),
          activeSyncProgress: null
        }));
        fetchSyncDetails();
        fetchDataAvailability();
      }
    };

    // Register listener and get cleanup function
    const cleanup = addSSEListener(`DeviceHistory-${deviceId}`, handleSSEMessage);

    return cleanup;
  }, [addSSEListener, deviceId]);

  // Custom date range is now initialized in fetchDataAvailability
  // This ensures it happens at the right time with valid data

  // Auto-fetch when time range or custom date range changes
  useEffect(() => {
    // Only fetch if we have valid data (not initial load)
    if (historyData !== null || !loading) {
      fetchHistoryData();
    }
  }, [selectedTimeRange, customDateRange]);

  const updateSyncProgress = (data) => {
    setSyncDetails(prev => ({
      ...prev,
      currentStatus: 'syncing',
      activeSyncProgress: {
        percentComplete: data.progress?.percentComplete || 0,
        recordsReceived: data.progress?.records || 0,
        startTime: prev?.activeSyncProgress?.startTime || new Date().toISOString()
      }
    }));
  };

  const fetchSyncDetails = async () => {
    if (vendor) return;
    try {
      const response = await axios.get(`/v1/history/${deviceId}/sync-details`);
      setSyncDetails(response.data.data);
    } catch (err) {
      console.error('Error fetching sync details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataAvailability = async () => {
    try {
      // Vendors get a slim boundaries-only endpoint; staff read the full
      // sync-details (device-reported boundaries included)
      const response = vendor
        ? await axios.get(`/vendor/history/${deviceId}/range`)
        : await axios.get(`/v1/history/${deviceId}/sync-details`);
      const boundaries = vendor
        ? response.data.data
        : response.data.data?.dataBoundaries;
      if (boundaries) {
        setDataAvailability(boundaries);

        // If we have actual data boundaries and custom date range not yet set, initialize it
        if (boundaries.oldestData && boundaries.newestData && (!customDateRange.start || !customDateRange.end)) {
          // Default to showing last 7 days of available data or all data if less than 7 days
          const newest = new Date(boundaries.newestData);
          const oldest = new Date(boundaries.oldestData);
          const sevenDaysAgo = subDays(newest, 7);

          setCustomDateRange({
            start: sevenDaysAgo > oldest ? sevenDaysAgo : oldest,
            end: newest
          });
        }
      }
    } catch (err) {
      console.error('Error fetching data availability:', err);
      // If the endpoint fails, try to get some data to determine boundaries
      try {
        const testResponse = await axios.get(`${vendor ? '/vendor' : '/v1'}/history/${deviceId}/data`, {
          params: {
            startTime: subDays(new Date(), 30).toISOString(),
            endTime: new Date().toISOString(),
            limit: 2,
            sortOrder: 'asc'
          }
        });
        
        if (testResponse.data.data?.records?.length > 0) {
          const records = testResponse.data.data.records;
          setDataAvailability({
            oldestData: records[0].recordedAt,
            newestData: records[records.length - 1].recordedAt,
            totalRecords: testResponse.data.data.total || records.length,
            pendingSync: 0
          });
        }
      } catch (testErr) {
        // Fallback to defaults
        setDataAvailability({
          oldestData: subDays(new Date(), 7).toISOString(),
          newestData: new Date().toISOString(),
          totalRecords: 0,
          pendingSync: 0
        });
      }
    }
  };

  const fetchHistoryData = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setDataLoading(true);
      }

      let startTime, endTime;
      const now = new Date();
      
      switch (selectedTimeRange) {
        case '1h':
          startTime = subHours(now, 1);
          endTime = now;
          break;
        case '2h':
          startTime = subHours(now, 2);
          endTime = now;
          break;
        case '4h':
          startTime = subHours(now, 4);
          endTime = now;
          break;
        case '6h':
          startTime = subHours(now, 6);
          endTime = now;
          break;
        case '12h':
          startTime = subHours(now, 12);
          endTime = now;
          break;
        case '24h':
          startTime = subDays(now, 1);
          endTime = now;
          break;
        case '2d':
          startTime = subDays(now, 2);
          endTime = now;
          break;
        case '3d':
          startTime = subDays(now, 3);
          endTime = now;
          break;
        case '7d':
          startTime = subDays(now, 7);
          endTime = now;
          break;
        case '14d':
          startTime = subDays(now, 14);
          endTime = now;
          break;
        case '30d':
          startTime = subDays(now, 30);
          endTime = now;
          break;
        case 'custom':
          if (customDateRange.start && customDateRange.end) {
            startTime = customDateRange.start;
            endTime = customDateRange.end;
          } else {
            // Fallback to last 24h if custom range not set
            startTime = subDays(now, 1);
            endTime = now;
          }
          break;
        default:
          startTime = subDays(now, 1);
          endTime = now;
      }

      // The endpoint pages. A fixed record cap handed back the oldest part of
      // a long window and quietly dropped the newest, which is why the
      // multi-day views stopped a day and a half in. Keep asking, oldest
      // first, until the whole window is here, with a ceiling for safety.
      const pageSize = 10000;
      const url = `${vendor ? '/vendor' : '/v1'}/history/${deviceId}/data`;
      let data = null;
      let records = [];
      do {
        const response = await axios.get(url, {
          params: {
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            limit: pageSize,
            skip: records.length,
            sortOrder: 'asc'
          }
        });
        data = response.data.data;
        records = records.concat(data.records || []);
        if (!data.records || data.records.length < pageSize) break;
      } while (records.length < data.total && records.length < 100000);

      setHistoryData({ ...data, records });
      setError(null);
    } catch (err) {
      console.error('Error fetching history data:', err);
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setDataLoading(false);
      setRefreshing(false);
    }
  };

  // CSV is the spreadsheet view of the window; JSON ships the records exactly
  // as stored, every field, temperatures in Celsius.
  const exportHistory = (exportFormat) => {
    const records = historyData?.records || [];
    const stamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    const base = `history_${deviceId}_${selectedTimeRange}_${stamp}`;
    let blob;
    let filename;

    if (exportFormat === 'csv') {
      const columns = exportColumns(tempUnit);
      const cell = (record, column) => {
        const value = record[column.key];
        if (value === null || value === undefined) return '';
        if (column.key === 'recordedAt') return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
        if (column.temperature) {
          const converted = toDisplayTemp(value, tempUnit);
          if (converted === null) return '';
          // A reading of exactly 0 F is stored as -17.78 C and converts back a
          // hair under zero, which toFixed would print as "-0.0".
          const text = converted.toFixed(1);
          return text === '-0.0' ? '0.0' : text;
        }
        if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
        return value;
      };

      const content = [
        columns.map((column) => column.label).join(','),
        ...records.map((record) => columns.map((column) => cell(record, column)).join(','))
      ].join('\n');

      blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      filename = `${base}.csv`;
    } else {
      blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
      filename = `${base}.json`;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const forceSync = async (priority = 'high') => {
    setError(null);
    try {
      await axios.post(`/v1/history/${deviceId}/trigger-sync`);
      
      setSyncDetails(prev => ({
        ...prev,
        currentStatus: 'syncing',
        activeSyncProgress: {
          percentComplete: 0,
          recordsReceived: 0,
          startTime: new Date().toISOString()
        }
      }));
    } catch (err) {
      console.error('Error forcing sync:', err);
      setError(err.response?.data?.error || err.message);
    }
  };

  const getSyncStatusInfo = (syncDetails) => {
    if (!syncDetails) {
      return {
        color: 'text-gray-600',
        icon: <ClockIcon className="w-5 h-5" />,
        label: 'Unknown',
        description: 'Sync status unavailable'
      };
    }

    const { currentStatus, lastSyncTime, dataBoundaries, activeSyncProgress } = syncDetails;
    
    if (currentStatus === 'syncing' || activeSyncProgress) {
      return {
        color: 'text-blue-600',
        icon: <ArrowPathIcon className="w-5 h-5 animate-spin" />,
        label: 'Syncing',
        description: `${activeSyncProgress?.percentComplete || 0}% complete`
      };
    }
    
    if (!dataBoundaries || !lastSyncTime) {
      return {
        color: 'text-yellow-600',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />,
        label: 'Never Synced',
        description: 'No data synchronized yet'
      };
    }
    
    const dataAge = differenceInMinutes(new Date(), new Date(dataBoundaries.newestData));
    
    if (dataAge < 5) {
      return {
        color: 'text-green-600',
        icon: <CheckCircleIcon className="w-5 h-5" />,
        label: 'Real-time',
        description: 'Data is up to date'
      };
    } else if (dataAge < 60) {
      return {
        color: 'text-green-600',
        icon: <CheckCircleIcon className="w-5 h-5" />,
        label: 'Recent',
        description: `Last data: ${dataAge} minutes ago`
      };
    } else if (dataAge < 1440) { // 24 hours
      return {
        color: 'text-yellow-600',
        icon: <ClockIcon className="w-5 h-5" />,
        label: 'Delayed',
        description: `Last data: ${Math.floor(dataAge / 60)} hours ago`
      };
    } else {
      return {
        color: 'text-red-600',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />,
        label: 'Outdated',
        description: `Last data: ${Math.floor(dataAge / 1440)} days ago`
      };
    }
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Enhanced data aggregation for better visualization
  const aggregateData = (records, timeRange) => {
    if (!records || records.length === 0) return records;

    const sortedRecords = [...records].sort((a, b) => 
      new Date(a.recordedAt) - new Date(b.recordedAt)
    );

    // Determine aggregation method based on time range and user preference
    let aggregationMethod = dataAggregation;
    if (aggregationMethod === 'auto') {
      const hoursDiff = differenceInHours(
        new Date(sortedRecords[sortedRecords.length - 1].recordedAt),
        new Date(sortedRecords[0].recordedAt)
      );
      
      if (hoursDiff <= 6) {
        aggregationMethod = 'raw';
      } else if (hoursDiff <= 72) { // 3 days
        aggregationMethod = 'hourly';
      } else {
        aggregationMethod = 'daily';
      }
    }

    if (aggregationMethod === 'raw') {
      // For raw data, just limit the number of points for performance
      const maxPoints = 500; // Increased for better detail
      let filteredRecords = sortedRecords;
      if (sortedRecords.length > maxPoints) {
        const step = Math.ceil(sortedRecords.length / maxPoints);
        filteredRecords = sortedRecords.filter((_, index) => index % step === 0);
      }
      // Ensure mode and dryerState are included in raw records
      return filteredRecords.map(r => ({
        ...r,
        apt: Number.isFinite(parseFloat(r.apt)) ? parseFloat(r.apt) : null,
        mode: r.mode || 'unknown',
        dryerState: r.dryerState ?? r.dryer_state ?? 'unknown'
      }));
    }

    // Aggregate data by hour or day
    const aggregatedMap = new Map();

    // Only rows recorded while the dryer was running go into the averages -
    // fans, burner and discharge all on, the same rule the controller applies
    // before it trusts a reading. An idle dryer keeps logging the same grain
    // sitting on the sensor for hours, and left in, those rows flatten the
    // average and pin the range to one motionless sample. The bucket itself is
    // kept for every hour that has rows, so its state and count still describe
    // the whole period; only the numbers go blank when nothing was running.
    // A missing reading stays out too, rather than being averaged in as zero.
    const collect = (bucket, value) => {
      const number = parseFloat(value);
      if (Number.isFinite(number)) bucket.push(number);
    };

    sortedRecords.forEach(record => {
      const date = new Date(record.recordedAt);
      let key;

      if (aggregationMethod === 'hourly') {
        key = format(startOfHour(date), 'yyyy-MM-dd HH:00');
      } else { // daily
        key = format(startOfDay(date), 'yyyy-MM-dd');
      }

      if (!aggregatedMap.has(key)) {
        aggregatedMap.set(key, {
          recordedAt: aggregationMethod === 'hourly' ? startOfHour(date).toISOString() : startOfDay(date).toISOString(),
          inletMoisture: [],
          outletMoisture: [],
          dischargeRate: [],
          apt: [],
          moistureTarget: record.moistureTarget || record.moisture_target,
          modes: [],
          dryerStates: [],
          count: 0,
          runningCount: 0
        });
      }

      const agg = aggregatedMap.get(key);
      const state = record.dryerState ?? record.dryer_state;
      if (state === 'running') {
        collect(agg.inletMoisture, record.inletMoisture ?? record.inlet_moisture);
        collect(agg.outletMoisture, record.outletMoisture ?? record.outlet_moisture);
        collect(agg.dischargeRate, record.dischargeRate ?? record.discharge_rate);
        collect(agg.apt, record.apt);
        agg.runningCount++;
      }
      if (record.mode) agg.modes.push(record.mode);
      if (state) agg.dryerStates.push(state);
      agg.count++;
    });

    // Helper to get most frequent value
    const getMostFrequent = (arr) => {
      if (!arr || arr.length === 0) return 'unknown';
      const counts = {};
      arr.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
      return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    };

    // A bucket with no running rows keeps its place on the axis but plots
    // nothing - a gap in the line, not a point that quietly means "idle".
    const average = (values) =>
      values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;

    // Convert to array and calculate averages
    return Array.from(aggregatedMap.values()).map(agg => ({
      recordedAt: agg.recordedAt,
      inletMoisture: average(agg.inletMoisture),
      outletMoisture: average(agg.outletMoisture),
      dischargeRate: average(agg.dischargeRate),
      apt: average(agg.apt),
      moistureTarget: agg.moistureTarget,
      mode: getMostFrequent(agg.modes),
      dryerState: getMostFrequent(agg.dryerStates),
      dataPoints: agg.count,
      runningPoints: agg.runningCount
    }));
  };

  // Enhanced time formatting based on data density
  const getSmartTimeFormat = (records, timeRange) => {
    if (!records || records.length === 0) return 'HH:mm';
    
    const hoursDiff = differenceInHours(
      new Date(records[records.length - 1].recordedAt),
      new Date(records[0].recordedAt)
    );
    
    if (hoursDiff <= 6) {
      return 'HH:mm';
    } else if (hoursDiff <= 24) {
      return 'MMM dd HH:mm';
    } else if (hoursDiff <= 72) {
      return 'MMM dd HH:00';
    } else if (hoursDiff <= 168) { // 7 days
      return 'MMM dd';
    } else {
      return 'MMM dd';
    }
  };

  // Prepare enhanced chart data
  const prepareChartData = () => {
    if (!historyData?.records || historyData.records.length === 0) return null;

    const processedRecords = aggregateData(historyData.records, selectedTimeRange);
    const timeFormat = getSmartTimeFormat(processedRecords, selectedTimeRange);

    // Calculate smart tick settings based on data density
    const tickSettings = {
      maxTicksLimit: processedRecords.length > 100 ? 20 : processedRecords.length > 50 ? 15 : undefined,
      autoSkip: true,
      autoSkipPadding: 5
    };

    return {
      moistureData: {
        labels: processedRecords.map(record => 
          format(new Date(record.recordedAt), timeFormat)
        ),
        datasets: [
          {
            label: 'Inlet',
            data: processedRecords.map(record => record.inletMoisture),
            borderColor: '#5cb85c',
            backgroundColor: 'rgba(92, 184, 92, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: processedRecords.length > 50 ? 0 : 2,
            pointHoverRadius: 4,
            pointHitRadius: 10
          },
          {
            label: 'Outlet',
            data: processedRecords.map(record => record.outletMoisture),
            borderColor: '#428bca',
            backgroundColor: 'rgba(66, 139, 202, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: processedRecords.length > 50 ? 0 : 2,
            pointHoverRadius: 4,
            pointHitRadius: 10
          },
          {
            label: 'Setpoint',
            data: processedRecords.map(record => record.moistureTarget),
            borderColor: '#d9534f',
            backgroundColor: 'rgba(217, 83, 79, 0.1)',
            borderDash: [5, 5],
            borderWidth: 2,
            fill: false,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            // No points are drawn, so this only shapes the legend swatch:
            // a line, the way the phone marks the setpoint entry.
            pointStyle: 'line'
          }
        ]
      },
      dischargeData: {
        labels: processedRecords.map(record =>
          format(new Date(record.recordedAt), timeFormat)
        ),
        datasets: [
          {
            label: 'Discharge Rate',
            data: processedRecords.map(record => record.dischargeRate),
            borderColor: '#f0ad4e',
            backgroundColor: 'rgba(240, 173, 78, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: processedRecords.length > 50 ? 0 : 2,
            pointHoverRadius: 4,
            pointHitRadius: 10
          }
        ]
      },
      aptData: {
        labels: processedRecords.map(record =>
          format(new Date(record.recordedAt), timeFormat)
        ),
        datasets: [
          {
            label: 'Drying Temp',
            data: processedRecords.map(record => toDisplayTemp(record.apt, tempUnit)),
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: processedRecords.length > 50 ? 0 : 2,
            pointHoverRadius: 4,
            pointHitRadius: 10
          }
        ]
      },
      processedRecords,
      tickSettings
    };
  };

  const chartData = useMemo(() => prepareChartData(), [historyData, selectedTimeRange, dataAggregation, tempUnit]);

  // Generate mode region annotations for the chart
  const getModeAnnotations = useMemo(() => {
    if (!showModeOverlay || !chartData?.processedRecords || chartData.processedRecords.length === 0) {
      return {};
    }

    const modeColors = {
      automatic: 'rgba(34, 197, 94, 0.2)', // green
      auto: 'rgba(34, 197, 94, 0.2)',      // green
      manual: 'rgba(249, 115, 22, 0.2)',   // orange
      local: 'rgba(59, 130, 246, 0.2)',    // blue
      unknown: 'rgba(156, 163, 175, 0.15)' // gray
    };

    const records = chartData.processedRecords;

    // Collect the stretches where the mode stays the same
    const regions = [];
    let regionStart = 0;
    let currentMode = records[0]?.mode || 'unknown';

    for (let i = 1; i <= records.length; i++) {
      const recordMode = records[i]?.mode || 'unknown';

      if (recordMode !== currentMode || i === records.length) {
        const xMaxValue = i === records.length ? records.length - 1 : i;
        regions.push({
          start: regionStart,
          end: xMaxValue,
          mode: currentMode
        });
        regionStart = i;
        currentMode = recordMode;
      }
    }

    // Colour bands only - short mode stints made the printed names pile on
    // top of each other, and the legend strip above the charts already says
    // which colour is which.
    const annotations = {};
    regions.forEach((region) => {
      const annotationKey = `mode_${region.start}_${region.end}`;
      annotations[annotationKey] = {
        type: 'box',
        xMin: region.start,
        xMax: region.end,
        backgroundColor: modeColors[region.mode.toLowerCase()] || modeColors.unknown,
        borderWidth: 0
      };
    });

    return annotations;
  }, [showModeOverlay, chartData]);

  const moistureChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 750,
      easing: 'easeInOutQuart'
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            weight: '500'
          },
          boxWidth: 15,
          boxHeight: 15
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2) + '%';
            }
            // Add data points count if aggregated
            const dataPoint = chartData?.processedRecords?.[context.dataIndex];
            if (dataPoint?.dataPoints > 1) {
              label += ` (${dataPoint.runningPoints ?? dataPoint.dataPoints} of ${dataPoint.dataPoints} pts running)`;
            }
            return label;
          },
          afterBody: function(context) {
            const dataPoint = chartData?.processedRecords?.[context[0]?.dataIndex];
            if (dataPoint) {
              const lines = [];
              if (dataPoint.mode && dataPoint.mode !== 'unknown') {
                lines.push(`Mode: ${dataPoint.mode}`);
              }
              if (dataPoint.dryerState && dataPoint.dryerState !== 'unknown') {
                lines.push(`State: ${dataPoint.dryerState}`);
              }
              if (lines.length > 0) {
                return ['', ...lines];
              }
            }
            return [];
          }
        }
      },
      annotation: {
        annotations: getModeAnnotations
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Moisture (%)',
          font: {
            size: 12
          }
        },
        min: 0,
        max: 50
      },
      x: {
        ticks: {
          ...chartData?.tickSettings,
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 10
          }
        },
        grid: {
          display: false
        }
      }
    },
  }), [chartData, getModeAnnotations]);

  const dischargeChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 750,
      easing: 'easeInOutQuart'
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            weight: '500'
          },
          boxWidth: 15,
          boxHeight: 15
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(0);
            }
            // Add data points count if aggregated
            const dataPoint = chartData?.processedRecords?.[context.dataIndex];
            if (dataPoint?.dataPoints > 1) {
              label += ` (${dataPoint.runningPoints ?? dataPoint.dataPoints} of ${dataPoint.dataPoints} pts running)`;
            }
            return label;
          },
          afterBody: function(context) {
            const dataPoint = chartData?.processedRecords?.[context[0]?.dataIndex];
            if (dataPoint) {
              const lines = [];
              if (dataPoint.mode && dataPoint.mode !== 'unknown') {
                lines.push(`Mode: ${dataPoint.mode}`);
              }
              if (dataPoint.dryerState && dataPoint.dryerState !== 'unknown') {
                lines.push(`State: ${dataPoint.dryerState}`);
              }
              if (lines.length > 0) {
                return ['', ...lines];
              }
            }
            return [];
          }
        }
      },
      annotation: {
        annotations: getModeAnnotations
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Discharge Rate',
          font: {
            size: 12
          }
        },
        min: 0,
        // bushels-per-hour dryers run 0-10000: the axis grows to fit the
        // data, while percent-style rates keep the familiar 0-110 frame
        suggestedMax: 110
      },
      x: {
        ticks: {
          ...chartData?.tickSettings,
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 10
          }
        },
        grid: {
          display: false
        }
      }
    },
  }), [chartData, getModeAnnotations]);

  const aptChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 750,
      easing: 'easeInOutQuart'
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            weight: '500'
          },
          boxWidth: 15,
          boxHeight: 15
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += `${context.parsed.y.toFixed(1)}°${tempUnit}`;
            }
            // Add data points count if aggregated
            const dataPoint = chartData?.processedRecords?.[context.dataIndex];
            if (dataPoint?.dataPoints > 1) {
              label += ` (${dataPoint.runningPoints ?? dataPoint.dataPoints} of ${dataPoint.dataPoints} pts running)`;
            }
            return label;
          },
          afterBody: function(context) {
            const dataPoint = chartData?.processedRecords?.[context[0]?.dataIndex];
            if (dataPoint) {
              const lines = [];
              if (dataPoint.mode && dataPoint.mode !== 'unknown') {
                lines.push(`Mode: ${dataPoint.mode}`);
              }
              if (dataPoint.dryerState && dataPoint.dryerState !== 'unknown') {
                lines.push(`State: ${dataPoint.dryerState}`);
              }
              if (lines.length > 0) {
                return ['', ...lines];
              }
            }
            return [];
          }
        }
      },
      annotation: {
        annotations: getModeAnnotations
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: `Drying Temp (°${tempUnit})`,
          font: {
            size: 12
          }
        },
        min: tempUnit === 'F' ? 32 : 0,
        suggestedMax: tempUnit === 'F' ? 212 : 100
      },
      x: {
        ticks: {
          ...chartData?.tickSettings,
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 10
          }
        },
        grid: {
          display: false
        }
      }
    },
  }), [chartData, getModeAnnotations, tempUnit]);

  const syncStatusInfo = getSyncStatusInfo(syncDetails);

  // Calculate quick stats from current data
  const calculateStats = () => {
    if (!historyData?.records || historyData.records.length === 0) {
      return {
        avgInlet: null,
        minInlet: null,
        maxInlet: null,
        avgOutlet: null,
        minOutlet: null,
        maxOutlet: null,
        avgDischarge: null,
        dominantDryerState: null,
        dryerStatePercentage: 0,
        modeCounts: {},
        runningSamples: 0,
        totalSamples: 0
      };
    }

    const records = historyData.records;
    // The moisture and rate figures come from running rows only - same rule
    // as the chart averages. The state breakdown below deliberately counts
    // every row: "running 31% of the time" is the context those figures need.
    const running = records.filter(r => (r.dryerState ?? r.dryer_state) === 'running');
    const finite = (values) => values.filter(v => v !== null && v !== undefined && !isNaN(v));
    const inletValues = finite(running.map(r => r.inletMoisture ?? r.inlet_moisture));
    const outletValues = finite(running.map(r => r.outletMoisture ?? r.outlet_moisture));
    const dischargeValues = finite(running.map(r => r.dischargeRate ?? r.discharge_rate));

    // Calculate dryer state frequency
    const dryerStateCounts = {};
    records.forEach(r => {
      const state = r.dryerState ?? r.dryer_state;
      if (state) {
        dryerStateCounts[state] = (dryerStateCounts[state] || 0) + 1;
      }
    });
    const dominantDryerState = Object.keys(dryerStateCounts).length > 0
      ? Object.entries(dryerStateCounts).sort((a, b) => b[1] - a[1])[0]
      : null;

    // Calculate mode frequency
    const modeCounts = {};
    records.forEach(r => {
      const mode = r.mode;
      if (mode) {
        modeCounts[mode] = (modeCounts[mode] || 0) + 1;
      }
    });

    // null, not 0, when the window has no running time - the cards show a dash
    // rather than a moisture of 0.00% that never happened.
    const mean = (values) => values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    const lowest = (values) => values.length > 0 ? Math.min(...values) : null;
    const highest = (values) => values.length > 0 ? Math.max(...values) : null;

    return {
      avgInlet: mean(inletValues),
      minInlet: lowest(inletValues),
      maxInlet: highest(inletValues),
      avgOutlet: mean(outletValues),
      minOutlet: lowest(outletValues),
      maxOutlet: highest(outletValues),
      avgDischarge: mean(dischargeValues),
      dominantDryerState: dominantDryerState ? dominantDryerState[0] : null,
      dryerStatePercentage: dominantDryerState ? Math.round((dominantDryerState[1] / records.length) * 100) : 0,
      modeCounts,
      runningSamples: running.length,
      totalSamples: records.length
    };
  };

  // Stats cards: a figure, or a dash when there was no running time to measure
  const fmtStat = (value, digits, suffix = '') =>
    value === null || value === undefined ? '—' : `${value.toFixed(digits)}${suffix}`;

  const stats = useMemo(() => calculateStats(), [historyData]);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 flex-1">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-sm font-medium text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Enhanced Sync Status Card — staff only; vendors see the data, not
          the sync machinery behind it */}
      {!vendor && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CloudArrowDownIcon className="w-5 h-5" />
            Data Synchronization
          </h3>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
              syncStatusInfo.color === 'text-green-600' ? 'bg-green-100' :
              syncStatusInfo.color === 'text-blue-600' ? 'bg-blue-100' :
              syncStatusInfo.color === 'text-yellow-600' ? 'bg-yellow-100' :
              'bg-red-100'
            }`}>
              {syncStatusInfo.icon}
              {syncStatusInfo.label}
            </span>
            {hasRole(user?.role, 'developer') && (
              <button
                onClick={() => forceSync()}
                disabled={syncDetails?.currentStatus === 'syncing'}
                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncDetails?.currentStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SignalIcon className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-700">Sync Status</p>
            </div>
            <p className={`text-2xl font-bold ${syncStatusInfo.color}`}>
              {syncStatusInfo.label}
            </p>
            <p className="text-sm text-gray-600 mt-1">{syncStatusInfo.description}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ClockIcon className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-700">Last Sync</p>
            </div>
            <p className="text-lg font-semibold">
              {syncDetails?.lastSyncTime ? 
                format(new Date(syncDetails.lastSyncTime), 'HH:mm') : 
                '--:--'
              }
            </p>
            <p className="text-sm text-gray-600">
              {syncDetails?.lastSyncTime ? 
                format(new Date(syncDetails.lastSyncTime), 'MMM dd, yyyy') : 
                'Never synced'
              }
            </p>
          </div>

          {syncDetails?.dataBoundaries && syncDetails.dataBoundaries.oldestData && syncDetails.dataBoundaries.newestData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDaysIcon className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-medium text-gray-700">Data Coverage</p>
              </div>
              <p className="text-lg font-semibold">
                {(() => {
                  try {
                    const oldest = new Date(syncDetails.dataBoundaries.oldestData);
                    const newest = new Date(syncDetails.dataBoundaries.newestData);
                    
                    if (isNaN(oldest.getTime()) || isNaN(newest.getTime())) {
                      return 'Unknown';
                    }
                    
                    const days = Math.ceil((newest - oldest) / (1000 * 60 * 60 * 24));
                    if (days > 365) return `${Math.floor(days / 365)} years`;
                    if (days > 30) return `${Math.floor(days / 30)} months`;
                    return `${days} days`;
                  } catch (err) {
                    return 'Unknown';
                  }
                })()}
              </p>
              <p className="text-sm text-gray-600">
                {(() => {
                  try {
                    return `${format(new Date(syncDetails.dataBoundaries.oldestData), 'MMM dd, yyyy')} - 
                            ${format(new Date(syncDetails.dataBoundaries.newestData), 'MMM dd, yyyy')}`;
                  } catch (err) {
                    return 'Date range unavailable';
                  }
                })()}
              </p>
              {syncDetails.dataBoundaries.totalRecords > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  {syncDetails.dataBoundaries.totalRecords.toLocaleString()} records
                  {syncDetails.dataBoundaries.source && ` (from ${syncDetails.dataBoundaries.source})`}
                </p>
              )}
            </div>
          )}

          {syncDetails?.dataBoundaries && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ServerIcon className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-medium text-gray-700">Total Records</p>
              </div>
              <p className="text-lg font-semibold">
                {syncDetails.dataBoundaries.totalRecords?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-600">
                {syncDetails.dataBoundaries.pendingSync > 0 && (
                  <span className="text-yellow-600">
                    {syncDetails.dataBoundaries.pendingSync.toLocaleString()} pending
                  </span>
                )}
                {syncDetails.dataBoundaries.pendingSync === 0 && 'All synced'}
              </p>
            </div>
          )}
        </div>

        {syncDetails?.activeSyncProgress && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-blue-900">Sync in Progress</p>
              <p className="text-sm text-blue-700">
                {formatDuration(Date.now() - new Date(syncDetails.activeSyncProgress.startTime))}
              </p>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 flex items-center justify-center"
                style={{ width: `${syncDetails.activeSyncProgress.percentComplete || 0}%` }}
              >
                {syncDetails.activeSyncProgress.percentComplete > 10 && (
                  <span className="text-xs text-white font-medium">
                    {syncDetails.activeSyncProgress.percentComplete}%
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-blue-700">
              {syncDetails.activeSyncProgress.recordsReceived.toLocaleString()} records synchronized
            </p>
          </div>
        )}

        {syncDetails?.lastError?.message && (
          <div className="mt-4 p-3 bg-red-50 rounded-md">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800">
                  <span className="font-medium">Sync Error:</span> {syncDetails.lastError.message}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {format(new Date(syncDetails.lastError.timestamp), 'MMM dd, yyyy HH:mm')}
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      )}

      {/* Enhanced Historical Data Charts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5" />
            Historical Data
          </h3>

          <div className="flex flex-wrap items-center gap-2">
            {/* Download the loaded window */}
            <DropdownMenu
              trigger={({ toggle }) => (
                <button
                  onClick={toggle}
                  disabled={!historyData?.records?.length}
                  className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download the history shown, every sample"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Download
                </button>
              )}
            >
              <button
                onClick={() => exportHistory('csv')}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Download as CSV
              </button>
              <button
                onClick={() => exportHistory('json')}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Download as JSON
              </button>
            </DropdownMenu>
            {/* Manual Refresh Button */}
            <button
              onClick={() => fetchHistoryData(true)}
              disabled={refreshing || dataLoading}
              className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh data for selected time range"
            >
              <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            {/* Data Aggregation Selector */}
            <select
              value={dataAggregation}
              onChange={(e) => setDataAggregation(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-md text-xs"
              title="Data aggregation method"
            >
              <option value="auto">Auto Aggregate</option>
              <option value="raw">Raw Data</option>
              <option value="hourly">Hourly Avg</option>
              <option value="daily">Daily Avg</option>
            </select>

            {/* Time Range Selector */}
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              title="Select time range, then click Refresh to load data"
            >
              <option value="1h">Last 1 Hour</option>
              <option value="2h">Last 2 Hours</option>
              <option value="4h">Last 4 Hours</option>
              <option value="6h">Last 6 Hours</option>
              <option value="12h">Last 12 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="2d">Last 2 Days</option>
              <option value="3d">Last 3 Days</option>
              <option value="7d">Last 7 Days</option>
              <option value="14d">Last 14 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {/* Data Availability Indicator */}
            {dataAvailability && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md">
                <InformationCircleIcon className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-600">
                  {format(new Date(dataAvailability.oldestData), 'MMM dd')} - {format(new Date(dataAvailability.newestData), 'MMM dd')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Performance Warning for Large Time Ranges */}
        {['14d', '30d'].includes(selectedTimeRange) && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-800">
                  <strong>Large time range selected:</strong> Loading {selectedTimeRange === '14d' ? '14 days' : '30 days'} of data may take longer.
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Tip: Use shorter time ranges (1-7 days) for faster loading, or the custom range to select specific dates.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Custom Date Range Selector */}
        {selectedTimeRange === 'custom' && customDateRange.start && customDateRange.end && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="mb-3 flex items-center gap-2 text-sm text-blue-800">
              <InformationCircleIcon className="w-4 h-4" />
              <span>Adjust your date range below, then click the <strong>Refresh</strong> button to load data</span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                <input
                  type="datetime-local"
                  value={format(customDateRange.start, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (!isNaN(newDate.getTime())) {
                      setCustomDateRange(prev => ({ ...prev, start: newDate }));
                    }
                  }}
                  min={dataAvailability ? format(new Date(dataAvailability.oldestData), "yyyy-MM-dd'T'HH:mm") : undefined}
                  max={dataAvailability ? format(new Date(dataAvailability.newestData), "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                <input
                  type="datetime-local"
                  value={format(customDateRange.end, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (!isNaN(newDate.getTime())) {
                      setCustomDateRange(prev => ({ ...prev, end: newDate }));
                    }
                  }}
                  min={format(customDateRange.start, "yyyy-MM-dd'T'HH:mm")}
                  max={dataAvailability ? format(new Date(dataAvailability.newestData), "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            {dataAvailability && (
              <div className="mt-3 space-y-1">
                <p className="text-xs text-gray-500">
                  Available data: {format(new Date(dataAvailability.oldestData), 'MMM dd, yyyy HH:mm')} - {format(new Date(dataAvailability.newestData), 'MMM dd, yyyy HH:mm')}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCustomDateRange({
                      start: new Date(dataAvailability.oldestData),
                      end: new Date(dataAvailability.newestData)
                    })}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Select Full Range
                  </button>
                  <span className="text-xs text-gray-400">|</span>
                  <button
                    onClick={() => {
                      const end = new Date(dataAvailability.newestData);
                      const start = subDays(end, 7);
                      setCustomDateRange({
                        start: start < new Date(dataAvailability.oldestData) ? new Date(dataAvailability.oldestData) : start,
                        end
                      });
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Last 7 Days of Data
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Stats */}
        {!dataLoading && historyData?.records && historyData.records.length > 0 && (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {/* Inlet Moisture Card */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <p className="text-xs font-medium text-green-700">Inlet Moisture</p>
              </div>
              <p className="text-lg font-bold text-green-800">{fmtStat(stats.avgInlet, 2, '%')}</p>
              <p className="text-xs text-green-600">
                {fmtStat(stats.minInlet, 2, '%')} - {fmtStat(stats.maxInlet, 2, '%')}
              </p>
            </div>

            {/* Outlet Moisture Card */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <p className="text-xs font-medium text-blue-700">Outlet Moisture</p>
              </div>
              <p className="text-lg font-bold text-blue-800">{fmtStat(stats.avgOutlet, 2, '%')}</p>
              <p className="text-xs text-blue-600">
                {fmtStat(stats.minOutlet, 2, '%')} - {fmtStat(stats.maxOutlet, 2, '%')}
              </p>
            </div>

            {/* Discharge Rate Card */}
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <p className="text-xs font-medium text-amber-700">Avg Discharge</p>
              </div>
              <p className="text-lg font-bold text-amber-800">{fmtStat(stats.avgDischarge, 1, '%')}</p>
              <p className="text-xs text-amber-600">discharge rate</p>
            </div>

            {/* Dryer State Card */}
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                <p className="text-xs font-medium text-purple-700">Dryer State</p>
              </div>
              <p className="text-lg font-bold text-purple-800 capitalize">{stats.dominantDryerState || 'N/A'}</p>
              <p className="text-xs text-purple-600">
                {stats.dryerStatePercentage}% of time
              </p>
            </div>

            {/* Mode Card with Breakdown */}
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200 col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                  <p className="text-xs font-medium text-indigo-700">Mode Breakdown</p>
                </div>
                <button
                  onClick={() => setShowModeOverlay(!showModeOverlay)}
                  className={`text-xs px-2 py-0.5 rounded transition-colors ${
                    showModeOverlay
                      ? 'bg-indigo-600 text-white'
                      : 'bg-indigo-200 text-indigo-700 hover:bg-indigo-300'
                  }`}
                  title="Toggle mode regions on chart"
                >
                  {showModeOverlay ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="space-y-1">
                {stats.modeCounts && Object.keys(stats.modeCounts).length > 0 ? (
                  Object.entries(stats.modeCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([mode, count]) => {
                      const percentage = historyData?.records ? Math.round((count / historyData.records.length) * 100) : 0;
                      const modeColors = {
                        automatic: 'bg-green-500',
                        auto: 'bg-green-500',
                        manual: 'bg-orange-500',
                        local: 'bg-blue-500',
                        unknown: 'bg-gray-400'
                      };
                      const bgColor = modeColors[mode.toLowerCase()] || 'bg-gray-400';
                      return (
                        <div key={mode} className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${bgColor}`}></div>
                          <span className="text-xs text-indigo-800 capitalize flex-1">{mode}</span>
                          <span className="text-xs font-semibold text-indigo-600">{percentage}%</span>
                        </div>
                      );
                    })
                ) : (
                  <p className="text-xs text-indigo-600">No mode data</p>
                )}
              </div>
            </div>

            <p className="col-span-2 sm:col-span-3 lg:col-span-5 text-xs text-gray-500">
              {stats.runningSamples > 0
                ? `Moisture and discharge figures cover running time only (${stats.runningSamples.toLocaleString()} of ${stats.totalSamples.toLocaleString()} samples). Hourly and daily averages use the same rule; Raw Data shows everything.`
                : 'The dryer was not running at any point in this window, so there are no moisture or discharge figures to show. Raw Data still shows every sample.'}
            </p>
          </div>
        )}

        {/* Mode Overlay Legend */}
        {showModeOverlay && !dataLoading && historyData?.records && historyData.records.length > 0 && (
          <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-xs font-medium text-indigo-700">Mode Regions:</span>
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-green-500/30"></div>
                <span className="text-xs text-gray-700">Automatic</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-orange-500/30"></div>
                <span className="text-xs text-gray-700">Manual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-blue-500/30"></div>
                <span className="text-xs text-gray-700">Local</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {dataLoading && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-600" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">Loading historical data...</p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedTimeRange === 'custom'
                  ? 'Fetching custom date range'
                  : `Fetching ${selectedTimeRange} of data`}
              </p>
            </div>
          </div>
        )}

        {/* Charts */}
        {!dataLoading && chartData && (
          <div className="space-y-6">
            {/* Moisture Chart */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Moisture Levels</h4>
              <div className="h-64 md:h-80">
                <Line data={chartData.moistureData} options={moistureChartOptions} />
              </div>
            </div>

            {/* Discharge Rate Chart */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Discharge Rate</h4>
              <div className="h-48 md:h-64">
                <Line data={chartData.dischargeData} options={dischargeChartOptions} />
              </div>
            </div>

            {/* Drying Temperature Chart */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Drying Temperature</h4>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setTempUnit('F')}
                    className={`px-2 py-1 text-xs rounded ${
                      tempUnit === 'F' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    °F
                  </button>
                  <button
                    onClick={() => setTempUnit('C')}
                    className={`px-2 py-1 text-xs rounded ${
                      tempUnit === 'C' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    °C
                  </button>
                </div>
              </div>
              <div className="h-48 md:h-64">
                <Line data={chartData.aptData} options={aptChartOptions} />
              </div>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {!dataLoading && (!historyData?.records || historyData.records.length === 0) && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <ChartBarIcon className="w-12 h-12 mb-2" />
            <p className="text-sm">No data available for the selected time range</p>
            {dataAvailability && (
              <div className="mt-4 text-center">
                <p className="text-xs mb-2">
                  Available data: {format(new Date(dataAvailability.oldestData), 'MMM dd, yyyy')} to {format(new Date(dataAvailability.newestData), 'MMM dd, yyyy')}
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setSelectedTimeRange('24h')}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Show Last 24h
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTimeRange('custom');
                      setCustomDateRange({
                        start: new Date(dataAvailability.oldestData),
                        end: new Date(dataAvailability.newestData)
                      });
                    }}
                    className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Show All Available Data
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Data Summary and Info */}
        {!dataLoading && historyData?.records && historyData.records.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <InformationCircleIcon className="w-4 h-4" />
                <span>
                  Fetched {historyData.records.length.toLocaleString()} records
                  {chartData?.processedRecords && chartData.processedRecords.length !== historyData.records.length && (
                    <> • Displaying {chartData.processedRecords.length} points ({dataAggregation === 'auto' ? 'auto-aggregated' : dataAggregation})</>
                  )}
                </span>
              </div>
              {historyData.total > historyData.records.length && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-yellow-600">
                    More data available ({historyData.total.toLocaleString()} total)
                  </span>
                  <button
                    onClick={() => {
                      // Increase the limit and refetch
                      fetchHistoryData();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
            {historyData.records.length > 1 && (
              <div className="mt-2 text-xs text-gray-500">
                Data range: {format(new Date(historyData.records[0].recordedAt), 'MMM dd, yyyy HH:mm')} to {' '}
                {format(new Date(historyData.records[historyData.records.length - 1].recordedAt), 'MMM dd, yyyy HH:mm')}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DeviceHistory;